import { useState, useEffect } from 'react';
import { API } from '@/lib/api';
import { toast } from 'sonner';
import { Share2, Twitter, Linkedin, Mail, Copy, Gift, Users, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Navigation from '@/components/Navigation';
import confetti from 'canvas-confetti';

function ReferralProgram() {
  const [referralCode, setReferralCode] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReferralData();
  }, []);

  const loadReferralData = async () => {
    try {
      const [codeRes, statsRes] = await Promise.all([
        API.post('/referrals/generate'),
        API.get('/referrals/stats')
      ]);
      setReferralCode(codeRes.data.referral_code);
      setStats(statsRes.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load referral data');
      setLoading(false);
    }
  };

  const claimReward = async () => {
    try {
      await API.post('/referrals/claim');
      confetti({
        particleCount: 200,
        spread: 100,
        origin: { y: 0.5 }
      });
      toast.success('🎉 Premium activated for 1 month!');
      loadReferralData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim reward');
    }
  };

  const getReferralLink = () => {
    return `${window.location.origin}?ref=${referralCode}`;
  };

  const copyLink = () => {
    navigator.clipboard.writeText(getReferralLink());
    toast.success('Link copied to clipboard!');
  };

  const shareToTwitter = () => {
    const text = `I'm mastering negotiations with Negotiation Ninja AI! Join me and get 3 free AI roleplays. Use my code: ${referralCode}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(getReferralLink())}`;
    window.open(url, '_blank');
  };

  const shareToLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getReferralLink())}`;
    window.open(url, '_blank');
  };

  const shareViaEmail = () => {
    const subject = 'Master Negotiations with AI';
    const body = `I've been using Negotiation Ninja AI to practice negotiations and it's incredible! Join me with this link: ${getReferralLink()}\n\nUse code: ${referralCode}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  const progress = (stats?.successful_referrals / 3) * 100;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <Gift className="text-green-500 w-16 h-16 mx-auto mb-4" />
          <h1 className="font-['Outfit'] text-4xl font-bold mb-3" data-testid="referral-title">Invite Friends, Get Premium Free</h1>
          <p className="text-gray-400">Refer 3 friends and unlock 1 month of premium access</p>
        </div>

        <div className="ninja-card mb-8 bg-gradient-to-r from-green-500/10 to-transparent border-green-500/30">
          <div className="text-center mb-6">
            <h2 className="font-['Outfit'] text-2xl font-bold mb-4">Your Referral Progress</h2>
            <div className="flex justify-center items-end gap-4 mb-4">
              <div className="text-5xl font-bold text-green-500">{stats?.successful_referrals || 0}</div>
              <div className="text-2xl text-gray-400 mb-2">/ 3 friends</div>
            </div>
            <div className="progress-bar h-4 max-w-md mx-auto mb-4">
              <div className="progress-indicator" style={{ width: `${progress}%` }}></div>
            </div>
            <p className="text-gray-400 text-sm">
              {stats?.successful_referrals >= 3 
                ? '🎉 You\'ve unlocked your reward!' 
                : `${3 - (stats?.successful_referrals || 0)} more ${3 - (stats?.successful_referrals || 0) === 1 ? 'friend' : 'friends'} to go!`
              }
            </p>
          </div>

          {stats?.reward_eligible && !stats?.reward_claimed && (
            <Button 
              onClick={claimReward}
              className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-lg py-6"
              data-testid="claim-reward-button"
            >
              <Gift className="w-6 h-6 mr-2" /> Claim Your Free Month!
            </Button>
          )}

          {stats?.reward_claimed && (
            <div className="text-center py-4 bg-green-500/20 border border-green-500/40 rounded-lg">
              <CheckCircle className="text-green-500 w-8 h-8 mx-auto mb-2" />
              <p className="font-bold text-green-400">Premium Activated! Enjoy your free month 🎉</p>
            </div>
          )}
        </div>

        <div className="ninja-card mb-8">
          <h2 className="font-['Outfit'] text-2xl font-bold mb-4">Share Your Referral Link</h2>
          <div className="flex gap-3 mb-6">
            <Input
              value={getReferralLink()}
              readOnly
              className="flex-1 bg-black/50 border-white/20"
              data-testid="referral-link-input"
            />
            <Button 
              onClick={copyLink}
              variant="outline"
              className="border-white/20 hover:bg-white/10"
              data-testid="copy-link-button"
            >
              <Copy className="w-5 h-5" />
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Button
              onClick={shareToTwitter}
              className="bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white"
              data-testid="share-twitter-button"
            >
              <Twitter className="w-5 h-5 mr-2" /> Twitter
            </Button>
            <Button
              onClick={shareToLinkedIn}
              className="bg-[#0A66C2] hover:bg-[#094d92] text-white"
              data-testid="share-linkedin-button"
            >
              <Linkedin className="w-5 h-5 mr-2" /> LinkedIn
            </Button>
            <Button
              onClick={shareViaEmail}
              variant="outline"
              className="border-white/20 hover:bg-white/10"
              data-testid="share-email-button"
            >
              <Mail className="w-5 h-5 mr-2" /> Email
            </Button>
          </div>
        </div>

        <div className="ninja-card">
          <h2 className="font-['Outfit'] text-2xl font-bold mb-4 flex items-center gap-2">
            <Users className="text-green-500 w-6 h-6" />
            Your Referral Stats
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center py-4 bg-white/5 rounded-lg">
              <div className="text-3xl font-bold text-green-500">{stats?.total_referrals || 0}</div>
              <div className="text-sm text-gray-400 mt-1">Total Invites</div>
            </div>
            <div className="text-center py-4 bg-white/5 rounded-lg">
              <div className="text-3xl font-bold text-green-500">{stats?.successful_referrals || 0}</div>
              <div className="text-sm text-gray-400 mt-1">Signed Up</div>
            </div>
            <div className="text-center py-4 bg-white/5 rounded-lg">
              <div className="text-3xl font-bold text-yellow-500">{stats?.pending_referrals || 0}</div>
              <div className="text-sm text-gray-400 mt-1">Pending</div>
            </div>
          </div>
        </div>

        <div className="mt-8 ninja-card bg-gradient-to-r from-yellow-500/10 to-transparent border-yellow-500/30">
          <h3 className="font-['Outfit'] text-xl font-bold mb-3">How It Works</h3>
          <ol className="space-y-2 text-gray-300">
            <li>1. Share your unique link with friends</li>
            <li>2. They sign up and complete onboarding</li>
            <li>3. After 3 successful referrals, claim your free premium month!</li>
          </ol>
          <p className="text-sm text-gray-500 mt-4">* Both you and your friends get benefits. They get 3 free roleplays, you get premium!</p>
        </div>
      </div>
    </div>
  );
}

export default ReferralProgram;